/**
 * The writes behind the workspace-settings switches, shared by the Settings pages and the setup panel
 * (ACT2-1108), so a switch saves the same way wherever it is flipped. `toastOptions` places their toasts,
 * for a caller whose layout a default toast would cover.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ExternalToast, toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

/** The four Context Hub AI controls. Each switch sends only the flag it changes. */
export function useAiControlsMutation(toastOptions?: ExternalToast) {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const settingsOptions = orpc.settings.get.queryOptions({ input: {} });
  return useMutation(
    orpc.contextHub.updateSettings.mutationOptions({
      // A settings fetch that is still running read the settings before this save. Cancel it, or it
      // writes the older value over the saved one when it completes.
      onMutate: () => queryClient.cancelQueries({ queryKey: settingsOptions.queryKey }),
      onSuccess: (result) => {
        queryClient.setQueryData(settingsOptions.queryKey, { settings: result.settings });
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to update AI controls', toastOptions);
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: settingsOptions.queryKey }),
    }),
  );
}

/** "Allow dashboards to be shared publicly?" */
export function usePublicSharingMutation(toastOptions?: ExternalToast) {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  return useMutation(
    orpc.settings.update.mutationOptions({
      onSuccess: (result) => {
        if (result.settings) {
          queryClient.setQueryData(orpc.settings.get.queryOptions({ input: {} }).queryKey, {
            settings: result.settings,
          });
        }
      },
      onError: () => {
        toast.error('Failed to update public sharing setting', toastOptions);
      },
    }),
  );
}

/** Slack "Guest access". */
export function useSlackGuestAccessMutation(toastOptions?: ExternalToast) {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  return useMutation(
    orpc.settings.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.settings.get.queryOptions({ input: {} }).queryKey,
        });
        toast.success('Slack guest access updated', toastOptions);
      },
      onError: () => toast.error('Failed to update Slack guest access', toastOptions),
    }),
  );
}
